import { toExcelDate } from 'components/xlsx/excelCells';
import { ClassifierData } from 'types/classifiers';
import {
    CorrespondenceListExportItem,
    CorrespondencePartyDto,
    CorrespondencePartyRoleType,
} from 'types/correspondence';
import { getClassifierText, t } from 'utils/getTranslations';

export const mapCorrespondencesToCSVFormat = (
    correspondences: CorrespondenceListExportItem[],
    classifiers: Record<string, ClassifierData>
) => {
    return correspondences.map((correspondence) => {
        const senderReceiverParties = (correspondence.parties || []).filter(
            (p) =>
                p.roleTypeCode === CorrespondencePartyRoleType.SENDER ||
                p.roleTypeCode === CorrespondencePartyRoleType.RECEIVER
        );
        const clientParties = (correspondence.parties || []).filter(
            (p) => p.roleTypeCode === CorrespondencePartyRoleType.CLIENT
        );

        return {
            [t('correspondence.export.correspondence-number')]: correspondence.number,
            [t('correspondence.export.correspondence-name')]: correspondence.name,
            [t('correspondence.export.correspondence-type')]: getClassifierText(
                classifiers['CORRESPONDENCE_TYPE'],
                correspondence.typeCode
            ),
            [t('correspondence.export.correspondence-category')]: getClassifierText(
                classifiers['CORRESPONDENCE_CATEGORY'],
                correspondence.categoryTypeCode
            ),
            [t('correspondence.export.direction')]: getClassifierText(
                classifiers['CORRESPONDENCE_DIRECTION'],
                correspondence.directionTypeCode
            ),
            [t('correspondence.export.incoming-outgoing-date')]: toExcelDate(correspondence.date),
            [t('correspondence.export.document-date')]: toExcelDate(correspondence.documentDate),
            [t('correspondence.export.is-official')]: correspondence.isOfficial ? t('yes') : t('no'),
            [t('correspondence.export.official-reference-number')]: correspondence.officialReference,
            [t('correspondence.export.sender-recipient-employee')]: (correspondence.employees || [])
                .flatMap((e) => (e.employee?.name ? [e.employee.name] : []))
                .join(', '),
            [t('correspondence.export.sender-recipient-person')]: senderReceiverParties
                .flatMap((p) => (p.mainParty?.name ? [p.mainParty.name] : []))
                .join(', '),
            [t('correspondence.export.sender-recipient-company')]: senderReceiverParties
                .flatMap((p) => (p.extraParty?.name ? [p.extraParty.name] : []))
                .join(', '),
            [t('correspondence.export.external-address')]: senderReceiverParties
                .flatMap((p) => {
                    const addr = formatAddress(p);
                    return addr ? [addr] : [];
                })
                .join('; '),
            [t('correspondence.export.correspondence-notes')]: correspondence.note,
            [t('correspondence.export.is-handled')]: correspondence.isHandled
                ? t('correspondence.handled')
                : t('correspondence.not-handled'),
            [t('correspondence.export.status')]: getClassifierText(
                classifiers['CORRESPONDENCE_STATUS_TYPE'],
                correspondence.statusTypeCode
            ),
            [t('correspondence.export.connected-clients')]: clientParties
                .flatMap((p) => (p.mainParty?.name ? [p.mainParty.name] : []))
                .join(', '),
            [t('correspondence.export.connected-cases')]: (correspondence.matters || [])
                .flatMap((m) => (m.fullName ? [m.fullName] : []))
                .join(', '),
            [t('correspondence.export.connected-assignments')]: (correspondence.assignments || [])
                .flatMap((a) => (a.name ? [a.name] : []))
                .join(', '),
        };
    });
};

const formatAddress = (party: CorrespondencePartyDto): string => {
    const address = party.correspondencePartyAddress;
    if (!address) return '';

    const parts = [
        address.address1,
        address.address2,
        [address.zipCode, address.cityName].filter(Boolean).join(' '),
    ].filter(Boolean);

    return parts.join(', ');
};
